/**
 * useSEO – renders per-page SEO tags via react-helmet-async.
 *
 * Usage:
 *   useSEO({
 *     title:       'Page title | KaizIQ Resolve',
 *     description: 'Meta description (≤160 chars)',
 *     canonical:   'https://resolve.kaiziq.io/pricing',
 *     noIndex:     false,          // true blocks crawlers for this page
 *     ogType:      'website',      // or 'article'
 *     schema:      { ... }         // optional JSON-LD object (or array)
 *   })
 */

import { Helmet } from 'react-helmet-async';

const SITE_NAME  = 'KaizIQ Resolve';
const BASE_URL   = 'https://resolve.kaiziq.io';
const OG_IMAGE   = `${BASE_URL}/og-image.png`;
const TWITTER_HANDLE = '@KaizIQ';

export default function useSEO({
    title,
    description,
    canonical,
    noIndex  = false,
    ogType   = 'website',
    schema   = null,
}) {
    const fullTitle = title
        ? `${title} | ${SITE_NAME}`
        : `${SITE_NAME} — Smart Ticket Management for Modern Teams`;

    const canonicalUrl = canonical || BASE_URL;

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonicalUrl} />
            <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />

            {/* Open Graph */}
            <meta property="og:title"       content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url"         content={canonicalUrl} />
            <meta property="og:type"        content={ogType} />
            <meta property="og:image"       content={OG_IMAGE} />
            <meta property="og:site_name"   content={SITE_NAME} />

            {/* Twitter */}
            <meta name="twitter:card"        content="summary_large_image" />
            <meta name="twitter:site"        content={TWITTER_HANDLE} />
            <meta name="twitter:title"       content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image"       content={OG_IMAGE} />

            {/* JSON-LD schema */}
            {schema && (
                <script type="application/ld+json">
                    {JSON.stringify(Array.isArray(schema) ? schema : [schema])}
                </script>
            )}
        </Helmet>
    );
}
